import { LLP_BTB_FINDING_TYPES, LLP_BTB_GAP_REASONS, LLP_BTB_RULE_IDS, } from '../database/project.schema.js';
import z from 'zod';
const evidenceSchema = z.looseObject({ documentId: z.string(), pageIds: z.array(z.string()) });
const snapshotSchema = z.looseObject({
    rank: z.number().int().nonnegative(),
    isWinner: z.boolean(),
    documentId: z.string(),
    pageIds: z.array(z.string()),
    esn: z.string().optional(),
    asOfDate: z.string().optional(),
    engineTsn: z.number().optional(),
    engineCsn: z.number().optional(),
    rowCount: z.number().int().nonnegative(),
    logType: z.enum(['on', 'off']).optional(),
    kind: z.enum(['operator', 'shop']).optional(),
    sourceClass: z.enum(['llp-status', 'other-listing', 'build-record']),
    isSigned: z.boolean().optional(),
});
/**
 * DR-5162: the manufacture record the trace cites; see {@link LlpBtbManufactureEvidence}.
 * DR-5190: at least one page id, and `date` is a real `YYYY-MM-DD` calendar day (`z.iso.date()`).
 */
const manufactureEvidenceSchema = z.looseObject({
    documentId: z.string(),
    pageIds: z.array(z.string()).min(1),
    date: z.iso.date(),
    precision: z.enum(['day', 'month']),
});
const counterPointSchema = z.looseObject({
    date: z.string().optional(),
    tsn: z.number().optional(),
    csn: z.number().optional(),
});
const gapReasonSchema = z.enum(LLP_BTB_GAP_REASONS);
const findingTypeSchema = z.enum(LLP_BTB_FINDING_TYPES);
export const llpBtbRuleIdSchema = z.enum(LLP_BTB_RULE_IDS);
const ratingPeriodSchema = z.looseObject({
    thrustRating: z.string(),
    from: counterPointSchema.optional(),
    to: counterPointSchema.optional(),
    cycles: z.number().optional(),
    lifeLimitCycles: z.number().optional(),
});
/** The result of an LLP or a tracked part set (`LlpBtbPart['result']`). */
export const llpBtbPartResultSchema = z.enum(['BTB_COMPLETE', 'BTB_INCOMPLETE', 'GAP', 'LIMIT_EXCEEDED']);
/** A pushed check: it carries no `evidence`, which the pull route serves. */
export const llpBtbPushedCheckSchema = z.looseObject({
    ruleId: llpBtbRuleIdSchema,
    result: z.enum(['PASS', 'FAIL', 'FLAG', 'N/A', 'UNAVAILABLE']),
    reason: z.string().optional(),
});
/** The version key of a trace push: the persisted finalize generation (DR-3342) and the rules version. */
export const llpBtbVersionSchema = z.looseObject({
    generation: z.number().int().positive().max(2_147_483_647),
    rulesVersion: z.string(),
});
const partSchema = z.looseObject({
    partKey: z.string(),
    partNumber: z.string(),
    serialNumber: z.string(),
    name: z.string().optional(),
    formerPartNumbers: z.array(z.string()).optional(),
    class: z.enum(['A', 'B', 'C', 'unconfirmed']),
    classBasis: z.literal('partial-build-records').optional(),
    donorEsns: z.array(z.string()),
    result: llpBtbPartResultSchema,
    limitExceeded: z.boolean(),
    current: z.looseObject({
        tsn: z.number().optional(),
        csn: z.number().optional(),
        lifeLimitCycles: z.number().optional(),
        remainingCycles: z.number().optional(),
        limitSource: z.enum(['catalogue', 'status-sheet', 'concession', 'unknown']),
        noLimit: z.literal(true).optional(),
        thrustRating: z.string().optional(),
        lifeFractionUsed: z.number().optional(),
        ratingPeriods: z.array(ratingPeriodSchema).optional(),
    }),
    gaps: z.array(z.looseObject({
        from: counterPointSchema.optional(),
        to: counterPointSchema.optional(),
        esn: z.string().optional(),
        reason: gapReasonSchema,
    })),
    checks: z.array(llpBtbPushedCheckSchema),
    findingIds: z.array(z.string()),
    donorDeps: z
        .array(z.looseObject({
        esn: z.string(),
        projectId: z.string().optional(),
        generation: z.number().int().optional(),
    }))
        .optional(),
});
const findingKeySchema = z.discriminatedUnion('kind', [
    z.looseObject({
        kind: z.literal('engine-event'),
        esn: z.string(),
        documentType: z.string(),
        event: z.looseObject({
            type: z.string(),
            date: z.string().optional(),
            workOrder: z.string().optional(),
            operator: z.string().optional(),
            periodStart: z.string().optional(),
            periodEnd: z.string().optional(),
            source: z.string().optional(),
        }),
    }),
    z.looseObject({
        kind: z.literal('part'),
        partKey: z.string(),
        documentType: z.string(),
        concessionNumber: z.string().optional(),
    }),
]);
/** One finding of a trace push; the LLP and the tracked part set families share it. */
export const llpBtbFindingSchema = z.looseObject({
    findingId: z.string(),
    requestKey: z.string(),
    type: findingTypeSchema,
    severity: z.enum(['error', 'warning']),
    key: findingKeySchema,
    linkedPartKeys: z.array(z.string()),
    evidence: z.array(evidenceSchema),
    message: z.string(),
});
/**
 * The LLP back-to-birth trace of the working engine (DR-4974), one envelope per project.
 *
 * Versioned by `{ projectId, version.generation, version.rulesVersion }`: `generation` is the
 * persisted finalize counter (DR-3342), `rulesVersion` changes with any rule change. The envelope
 * carries the persisted finalize timestamp as `generatedAt`, so retries are byte-identical; a consumer orders
 * envelopes by `generation`. The per-part timeline, each check's `evidence` and the evidence list
 * are not pushed; they are served by `GET /api/projects/:id/llp-btb`. The schema is loose like
 * every family's, so the payload builder strips `timeline` and `checks[].evidence` itself.
 * `findings[]` holds one finding per rule and request; a part lists the findings it is linked to in
 * `findingIds`. LLP rows only: the stored trace's tracked part sets (DR-5467), their T rules and
 * their findings travel in `tracked-part-set-ready` (DR-5519), never here.
 *
 * A separate family from `llp-status-events-ready`, which is unchanged: `snapshots[]` lists every
 * LLP snapshot of the working engine in the election's own order, and its `isWinner` row is the
 * snapshot that family publishes.
 *
 * The push has two shapes (DR-5116), and exactly one validates. Under the ceiling (9,000,000 bytes
 * serialized; the body travels as one Pub/Sub message, limit 10,000,000 bytes) it carries `parts`
 * and never `partsOmitted` or `partCount`. Over the ceiling it carries the summary, the snapshots
 * and the findings without `parts`, sets `partsOmitted: true` and `partCount` (the stored part
 * count), and the consumer pulls the parts from `GET /api/projects/:id/llp-btb`.
 *
 * A retraction is a push with `snapshots`, `parts` and `findings` all empty, never `partsOmitted`:
 * the project has no LLP snapshot left, or it lost its engine serial after a stored trace (DR-5116;
 * `esn` is then the last traced engine).
 *
 * `manufactureEvidence` (DR-5162) rides in both shapes when the trace cites an agreed manufacture
 * record; a retraction never carries it.
 */
export const llpBtbReadyWebhookSchema = z
    .looseObject({
    type: z.literal('llp-btb-ready'),
    projectId: z.string(),
    generatedAt: z.iso.datetime(),
    esn: z.string(),
    version: llpBtbVersionSchema,
    rulesEvaluated: z.array(llpBtbRuleIdSchema),
    snapshots: z.array(snapshotSchema),
    parts: z.array(partSchema).optional(),
    partsOmitted: z.literal(true).optional(),
    partCount: z.number().int().nonnegative().optional(),
    findings: z.array(llpBtbFindingSchema),
    manufactureEvidence: manufactureEvidenceSchema.optional(),
})
    .superRefine((payload, context) => {
    const withParts = payload.parts !== undefined && payload.partsOmitted === undefined && payload.partCount === undefined;
    const partsOmitted = payload.parts === undefined && payload.partsOmitted === true && payload.partCount !== undefined;
    if (!withParts && !partsOmitted) {
        context.addIssue({
            code: 'custom',
            path: ['parts'],
            message: 'carry `parts` without `partsOmitted`/`partCount`, or omit `parts` with `partsOmitted: true` and `partCount`',
        });
    }
});
//# sourceMappingURL=llp-btb-ready.webhook.js.map